// src/hr/api.js
// Thin wrappers over the shared axios instance (baseURL '/api') for the Time Off + HR Portal API.
// Every call resolves to the response body. Employee routes live under /hr, HR-only routes under
// /hr/admin (server: routes/hrRoutes.js and routes/hrAdminRoutes.js).
import api from '../axiosInstance';

const API_BASE = String((api && api.defaults && api.defaults.baseURL) || '/api').replace(/\/+$/, '');

const body = (promise) => promise.then((res) => res.data);
const seg = (value) => encodeURIComponent(String(value));

// Drops empty values so optional filters never reach the server as "undefined".
function cleanParams(params) {
  const out = {};
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') out[key] = value;
  });
  return out;
}

const get = (url, params) => body(params === undefined ? api.get(url) : api.get(url, { params: cleanParams(params) }));
const post = (url, data) => body(data === undefined ? api.post(url) : api.post(url, data));
const put = (url, data) => body(api.put(url, data));
const patch = (url, data) => body(api.patch(url, data));
const del = (url, data) => body(data === undefined ? api.delete(url) : api.delete(url, { data }));

const A = '/hr/admin';

const admin = {
  people: {
    list: (params) => get(`${A}/people`, params || {}),
    create: (data) => post(`${A}/people`, data),
    get: (id, params) => get(`${A}/people/${seg(id)}`, params || {}),
    update: (id, data) => patch(`${A}/people/${seg(id)}`, data),
    archive: (id) => post(`${A}/people/${seg(id)}/archive`),
    legalHold: (id, data) => post(`${A}/people/${seg(id)}/legal-hold`, data),
    remove: (id, data) => del(`${A}/people/${seg(id)}`, data),
    restoreSick: (id, data) => post(`${A}/people/${seg(id)}/restore-sick`, data)
  },
  allowances: {
    put: (personId, year, data) => put(`${A}/people/${seg(personId)}/allowances/${seg(year)}`, data)
  },
  entries: {
    create: (personId, data) => post(`${A}/people/${seg(personId)}/entries`, data),
    update: (id, data) => patch(`${A}/entries/${seg(id)}`, data),
    remove: (id) => del(`${A}/entries/${seg(id)}`)
  },
  types: {
    list: () => get(`${A}/types`),
    create: (data) => post(`${A}/types`, data),
    update: (id, data) => patch(`${A}/types/${seg(id)}`, data)
  },
  holidays: {
    list: () => get(`${A}/holidays`),
    add: (data) => post(`${A}/holidays`, data),
    remove: (date) => del(`${A}/holidays/${seg(date)}`)
  },
  settings: {
    get: () => get(`${A}/settings`),
    update: (data) => patch(`${A}/settings`, data)
  },
  // Every HR person can read the list; only the owner can change it.
  access: {
    list: () => get(`${A}/access`),
    add: (data) => post(`${A}/access`, data),
    remove: (email) => del(`${A}/access/${seg(email)}`)
  },
  changelog: (params) => get(`${A}/changelog`, params || {}),
  // view: 'hr' (default) | 'manager' | 'decided'
  requests: {
    list: (params) => get(`${A}/requests`, params || {}),
    decide: (id, data) => post(`${A}/requests/${seg(id)}/decision`, data),
    cancel: (id) => post(`${A}/requests/${seg(id)}/cancel`)
  },
  // create / newVersion take a FormData (PDF in field `file`) or a plain object ({ url, ... }).
  policies: {
    list: () => get(`${A}/policies`),
    create: (data) => post(`${A}/policies`, data),
    newVersion: (id, data) => post(`${A}/policies/${seg(id)}/new-version`, data),
    status: (id) => get(`${A}/policies/${seg(id)}/status`),
    reissueNotices: (data) => post(`${A}/policies/reissue-notices`, data)
  },
  // kind: 'onboarding' | 'offboarding'
  checklistTemplates: {
    get: (kind) => get(`${A}/checklist-templates/${seg(kind)}`),
    put: (kind, data) => put(`${A}/checklist-templates/${seg(kind)}`, data)
  },
  checklists: {
    list: (params) => get(`${A}/checklists`, params || {}),
    start: (personId, data) => post(`${A}/people/${seg(personId)}/checklists`, data),
    setItem: (id, itemId, data) => patch(`${A}/checklists/${seg(id)}/items/${seg(itemId)}`, data)
  },
  reminders: {
    list: () => get(`${A}/reminders`),
    create: (data) => post(`${A}/reminders`, data),
    update: (id, data) => patch(`${A}/reminders/${seg(id)}`, data),
    remove: (id) => del(`${A}/reminders/${seg(id)}`)
  },
  yearEnd: {
    get: (fromYear) => get(`${A}/year-end/${seg(fromYear)}`),
    post: (fromYear, data) => post(`${A}/year-end/${seg(fromYear)}`, data)
  },
  // name: balances | usage | year-end | whos-out | overdue | ledger
  reports: {
    get: (name, params) => get(`${A}/reports/${seg(name)}`, params || {}),
    csvUrl: (name, params) => {
      const query = new URLSearchParams({ ...cleanParams(params), format: 'csv' });
      return `${API_BASE}${A}/reports/${seg(name)}?${query.toString()}`;
    }
  },
  // The notification bell's HR items: { items: [{ id, kind, text, link, at }] }
  notifications: () => get(`${A}/notifications`)
};

export const hr = {
  me: () => get('/hr/me'),
  // No dates = this week (Monday to Friday) on the server. { scope: 'team' } = only my direct reports.
  whosOut: (from, to, opts) => get('/hr/whos-out', { from, to, scope: opts && opts.scope }),
  // A manager's direct reports: { team: [{ id, firstName, lastName, title, department }] }; 403 for no team.
  myTeam: () => get('/hr/me/team'),
  myLedger: () => get('/hr/me/ledger'),
  submitRequest: (data) => post('/hr/me/requests', data),
  cancelRequest: (id) => post(`/hr/me/requests/${seg(id)}/cancel`),
  reportSick: (data) => post('/hr/me/sick-reports', data),
  approvals: () => get('/hr/approvals'),
  decide: (id, data) => post(`/hr/approvals/${seg(id)}/decision`, data),
  myPolicies: () => get('/hr/policies/mine'),
  ackPolicy: (id) => post(`/hr/policies/${seg(id)}/ack`),
  // For links and new tabs: the PDF is served inline by the API, never statically.
  policyFileUrl: (id) => `${API_BASE}/hr/policies/${seg(id)}/file`,
  admin
};

// The server's own message when it sent one ({ error }), otherwise the fallback.
export function errorText(err, fallback = 'Something went wrong') {
  const msg = err && err.response && err.response.data && err.response.data.error;
  return typeof msg === 'string' && msg ? msg : fallback;
}

export default hr;
